import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * Shared, deterministic menu_board renderer used identically by the device
 * player (LayoutRenderer) and the Menu Mapping preview. It consumes the
 * normalized payload built server-side by PlaylistSyncDraftService — the same
 * bytes reach preview and playback, so what you preview is what plays.
 *
 * It renders no network calls of its own and never touches Clover. Images are
 * pre-resolved absolute URLs (server-side, tenant-checked).
 */

export interface MenuBoardImage { media_id?: number | null; url?: string | null; }
export interface MenuBoardItem {
  id?: number;
  name: string;
  description?: string | null;
  formatted_price?: string | null;
  available?: boolean;
  sold_out_label?: string | null;
  badge?: string | null;
  highlighted?: boolean;
  image?: MenuBoardImage | null;
}
export interface MenuBoardCategory { id?: number; name: string; items: MenuBoardItem[]; }
export interface MenuBoardLayout {
  columns?: number;
  item_alignment?: "left" | "center" | "right";
  price_alignment?: "inline" | "right" | "next_line";
  image_position?: "left" | "right" | "top" | "none";
  show_image?: boolean;
  show_description?: boolean;
  show_currency_symbol?: boolean;
  max_name_lines?: number;
  max_description_lines?: number;
  overflow_behavior?: "clip" | "shrink" | "paginate";
  font_scale?: number;
  sold_out_style?: "strikethrough" | "badge" | "hide";
}
export interface MenuBoardPayload {
  type?: string;
  layout?: MenuBoardLayout;
  currency?: string;
  sold_out_label?: string;
  categories?: MenuBoardCategory[];
}

const clampLines = (n?: number): React.CSSProperties =>
  n && n > 0
    ? { display: "-webkit-box", WebkitLineClamp: n, WebkitBoxOrient: "vertical", overflow: "hidden" }
    : {};

/** Shrink-to-fit: scale content down (to a floor) until it fits the zone. */
function useFitScale(enabled: boolean, deps: React.DependencyList) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    if (!enabled) { setScale(1); return; }
    const o = outer.current, i = inner.current;
    if (!o || !i) return;
    setScale(1);
    // Measure on next frame so layout at scale 1 is settled.
    const raf = requestAnimationFrame(() => {
      const oh = o.clientHeight, ih = i.scrollHeight;
      const ow = o.clientWidth, iw = i.scrollWidth;
      if (ih <= oh && iw <= ow) return;
      const s = Math.max(0.5, Math.min(oh / ih, ow / iw));
      setScale(Number.isFinite(s) ? s : 1);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { outer, inner, scale };
}

const MenuBoard: React.FC<{ payload: MenuBoardPayload; textColor?: string }> = ({ payload, textColor = "#fff" }) => {
  const layout = payload.layout || {};
  const columns = Math.max(1, layout.columns || 1);
  const overflow = layout.overflow_behavior || "shrink";
  const categories = payload.categories || [];

  const { outer, inner, scale } = useFitScale(overflow === "shrink", [
    JSON.stringify(payload).length, columns, overflow,
  ]);

  const baseFont = 18 * (layout.font_scale || 1);

  return (
    <div
      ref={outer}
      style={{
        width: "100%", height: "100%", overflow: "hidden",
        color: textColor, fontFamily: "Inter, system-ui, sans-serif",
        padding: "3%", boxSizing: "border-box",
      }}
    >
      <div
        ref={inner}
        style={{
          transform: overflow === "shrink" ? `scale(${scale})` : undefined,
          transformOrigin: "top left",
          width: overflow === "shrink" ? `${100 / scale}%` : "100%",
          fontSize: baseFont,
        }}
      >
        {categories.length === 0 && (
          <div style={{ opacity: 0.6, fontSize: baseFont * 0.9 }}>No categories mapped to this zone.</div>
        )}
        {categories.map((cat, ci) => (
          <div key={cat.id ?? ci} style={{ marginBottom: "1.2em", breakInside: "avoid" }}>
            <div style={{
              fontWeight: 800, fontSize: "1.35em", letterSpacing: 0.3,
              borderBottom: "2px solid currentColor", paddingBottom: "0.15em", marginBottom: "0.5em",
              opacity: 0.95,
            }}>
              {cat.name}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`, gap: "0.6em 1.4em" }}>
              {cat.items.map((it, ii) => (
                <MenuItemRow key={it.id ?? ii} item={it} layout={layout} baseFont={baseFont} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MenuItemRow: React.FC<{ item: MenuBoardItem; layout: MenuBoardLayout; baseFont: number }> = ({ item, layout, baseFont }) => {
  const soldOut = item.available === false;
  const soldOutStyle = layout.sold_out_style || "strikethrough";
  if (soldOut && soldOutStyle === "hide") return null;

  const showImage = (layout.show_image ?? true) && (layout.image_position ?? "left") !== "none";
  const imgPos = layout.image_position ?? "left";
  const align = layout.item_alignment ?? "left";
  const priceAlign = layout.price_alignment ?? "right";

  const img = showImage && item.image?.url ? (
    <img src={item.image.url} alt="" style={{
      width: imgPos === "top" ? "100%" : "3.2em",
      height: imgPos === "top" ? "5em" : "3.2em",
      objectFit: "cover", borderRadius: 6, flexShrink: 0,
      opacity: soldOut ? 0.5 : 1,
    }} />
  ) : null;

  const nameEl = (
    <span style={{
      fontWeight: 700, fontSize: "1em",
      textDecoration: soldOut && soldOutStyle === "strikethrough" ? "line-through" : "none",
      ...clampLines(layout.max_name_lines),
    }}>
      {item.name}
      {item.badge && (
        <span style={{ marginLeft: "0.5em", fontSize: "0.62em", fontWeight: 800, padding: "0.1em 0.5em",
          borderRadius: 999, background: "rgba(212,175,55,0.9)", color: "#0B1E2D", verticalAlign: "middle" }}>
          {item.badge}
        </span>
      )}
      {soldOut && soldOutStyle === "badge" && (
        <span style={{ marginLeft: "0.5em", fontSize: "0.62em", fontWeight: 800, padding: "0.1em 0.5em",
          borderRadius: 999, background: "rgba(239,68,68,0.9)", color: "#fff", verticalAlign: "middle" }}>
          {item.sold_out_label || "Sold Out"}
        </span>
      )}
    </span>
  );

  const priceEl = item.formatted_price ? (
    <span style={{ fontWeight: 800, fontSize: "1em", whiteSpace: "nowrap", opacity: soldOut ? 0.6 : 1 }}>
      {item.formatted_price}
    </span>
  ) : null;

  const descEl = (layout.show_description ?? true) && item.description ? (
    <div style={{ fontSize: "0.72em", opacity: 0.7, marginTop: "0.1em", ...clampLines(layout.max_description_lines) }}>
      {item.description}
    </div>
  ) : null;

  const textBlock = (
    <div style={{ flex: 1, minWidth: 0, textAlign: align }}>
      <div style={{
        display: "flex",
        flexDirection: priceAlign === "next_line" ? "column" : "row",
        alignItems: priceAlign === "next_line" ? "stretch" : "baseline",
        justifyContent: priceAlign === "right" ? "space-between" : "flex-start",
        gap: priceAlign === "inline" ? "0.5em" : "0.75em",
      }}>
        {nameEl}
        {priceAlign !== "next_line" && priceEl}
      </div>
      {priceAlign === "next_line" && priceEl && <div style={{ marginTop: "0.1em" }}>{priceEl}</div>}
      {descEl}
    </div>
  );

  return (
    <div style={{
      display: "flex",
      flexDirection: imgPos === "top" ? "column" : "row",
      gap: "0.6em",
      alignItems: imgPos === "top" ? "stretch" : "flex-start",
      padding: item.highlighted ? "0.35em 0.5em" : 0,
      borderRadius: item.highlighted ? 8 : 0,
      background: item.highlighted ? "rgba(212,175,55,0.14)" : "transparent",
    }}>
      {(imgPos === "left" || imgPos === "top") && img}
      {textBlock}
      {imgPos === "right" && img}
    </div>
  );
};

export default MenuBoard;
